import { isBrokerAuthEnabled, loginRedirectFailed } from './auth';
import type { AccountInfo } from '@azure/msal-browser';
import AppConfig from '~/utils/appconfig';

/**
 * Azure Entra sign-in.
 *
 * Entra authenticates the application on the legacy path, and remains the only
 * issuer of Azure resource-audience tokens (Blob storage) on every path —
 * including when the application authenticates against the identity broker,
 * where broker sign-in runs through oidc-client-ts (~/lib/oidc.ts) instead.
 */
export const createMsalInstance = async () => {
    const { PublicClientApplication, EventType } = await import(
        '@azure/msal-browser'
    );

    const msalInstance = new PublicClientApplication({
        auth: {
            clientId: AppConfig.clientId,
            authority: AppConfig.authority,
            redirectUri: '/',
            postLogoutRedirectUri: '/',
            navigateToLoginRequestUrl: false, // Prevent redirect loops
        },
    });

    await msalInstance.initialize();
    let redirectAccount: AccountInfo | null = null;
    try {
        const redirectResult = await msalInstance.handleRedirectPromise();
        redirectAccount = redirectResult?.account ?? null;
    } catch (error) {
        // A sign-in that came back refused or cancelled is an outcome of that
        // one attempt, not of the instance, which stays usable for the next.
        console.error('Entra sign-in did not complete:', error);
        // Where Entra signs the application in, the login page must ask
        // before trying again, or a repeatable refusal loops the browser.
        if (!isBrokerAuthEnabled()) {
            loginRedirectFailed();
        }
    }

    // Set logged-in account as the active account, if login was successful
    msalInstance.addEventCallback((event) => {
        if (
            (event.eventType === EventType.LOGIN_SUCCESS ||
                event.eventType === EventType.SSO_SILENT_SUCCESS) &&
            event.payload
        ) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const payload = event.payload as any;
            const account = payload.account;
            if (account) {
                msalInstance.setActiveAccount(account);
            }
        }
    });

    // The account a sign-in has just returned with is the one the user chose,
    // so it wins over any other in the cache; failing that the account already
    // active stays so, and only then is any cached account taken. Taking the
    // first cached account outright could reinstate one the user had just
    // signed in to replace.
    const activeAccount =
        redirectAccount ??
        msalInstance.getActiveAccount() ??
        msalInstance.getAllAccounts()[0] ??
        null;
    if (activeAccount) {
        msalInstance.setActiveAccount(activeAccount);
    }

    return msalInstance;
};
